import { Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon, CircleCheckIcon } from '@persona-id/design-tokens/icons';
import dompurify from 'dompurify';
import { create, Delta, DiffPatcher, formatters } from 'jsondiffpatch';
import { isEmpty, omit, pick } from 'lodash/fp';
import { createRef, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import EditorNavItem from 'dashboard/apps/InquiryTemplateEditor/EditorNavItem';
import EditorNavItemGroup, {
  GroupScrollItemProps,
} from 'dashboard/apps/InquiryTemplateEditor/EditorNavItemGroup/EditorNavItemGroup';
import InquiriesEnhancedUsagesList from 'dashboard/apps/InquiryTemplateEditor/InquiriesEnhancedUsagesList';
import {
  LOAD_VERSION_DIFF_ERROR_MESSAGE,
  PANEL_WIDTH,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { ScrollRefMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { getVtmplvDisplayName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import { linkActiveCSS } from 'dashboard/components/EditorNavItemLink';
import Banner from 'dashboard/components-lib/Banner';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  BiometricConsentComplianceFailureReasons,
  EnhancedUsage,
  InquiryTemplateVersion,
  InquiryTemplateVersionAttributes,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionTier,
  InvalidStep,
} from 'dashboard/models/inquiry-template-version';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import {
  VerificationTemplateVersion,
  VerificationTemplateVersionAttributes,
} from 'dashboard/models/verification-template-version';
import {
  ActualWorkflowStep,
  WorkflowVersion,
  WorkflowVersionAttributes,
} from 'dashboard/models/workflow-version';
import { InquiryTemplateSchema } from 'dashboard/redux/slices/inquiry-template';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import defaultTheme, { Variant } from 'design-system/theme';
import { BASE } from 'design-system/variables/spacing';
import { FieldSchema } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

const FOOTER_HEIGHT = 56;

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const LeftColumnContainer = styled.div`
  display: flex;
  overflow: hidden;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
  flex: 0 0 ${PANEL_WIDTH}px;
  max-width: ${PANEL_WIDTH}px;
`;

const LeftColumn = styled.div`
  width: ${PANEL_WIDTH}px;
  padding: ${Spacing[5]};
`;

const DiffNavItem = styled(EditorNavItem)`
  &[aria-selected='true'] {
    ${linkActiveCSS}
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex-grow: 1;
  flex: auto;
  overflow-y: auto;
  height: 100%;
  padding: ${Spacing[5]};
  padding-bottom: ${FOOTER_HEIGHT + BASE * 6}px;
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: inherit;
`;

const RightColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const NoContentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const ScrollWrapper = styled.div``;

const DiffContainer = styled.div`
  padding: ${Spacing[2]};
  margin-top: ${Spacing[3]};
  margin-bottom: ${Spacing[3]};
  background-color: ${({ theme }) => theme.color.background.secondary};
  max-height: 600px;
  overflow-y: auto;
  // default highlight styling provided by jsondiffpatch
  .jsondiffpatch-delta {
    font-family: 'Bitstream Vera Sans Mono', 'DejaVu Sans Mono', Monaco, Courier, monospace;
    font-size: 12px;
    margin: 0;
    padding: 0 0 0 12px;
    display: inline-block;
  }
  .jsondiffpatch-delta pre {
    font-family: 'Bitstream Vera Sans Mono', 'DejaVu Sans Mono', Monaco, Courier, monospace;
    font-size: 12px;
    margin: 0;
    padding: 0;
    display: inline-block;
  }
  ul.jsondiffpatch-delta {
    list-style-type: none;
    padding: 0 0 0 20px;
    margin: 0;
  }
  .jsondiffpatch-delta ul {
    list-style-type: none;
    padding: 0 0 0 20px;
    margin: 0;
  }
  .jsondiffpatch-added .jsondiffpatch-property-name,
  .jsondiffpatch-added .jsondiffpatch-value pre,
  .jsondiffpatch-modified .jsondiffpatch-right-value pre,
  .jsondiffpatch-textdiff-added {
    background: #bbffbb;
  }
  .jsondiffpatch-deleted .jsondiffpatch-property-name,
  .jsondiffpatch-deleted pre,
  .jsondiffpatch-modified .jsondiffpatch-left-value pre,
  .jsondiffpatch-textdiff-deleted {
    background: #ffbbbb;
    text-decoration: line-through;
  }
  .jsondiffpatch-unchanged,
  .jsondiffpatch-movedestination {
    color: gray;
  }
  .jsondiffpatch-unchanged,
  .jsondiffpatch-movedestination > .jsondiffpatch-value {
    transition: all 0.5s;
    -webkit-transition: all 0.5s;
    overflow-y: hidden;
  }
  .jsondiffpatch-unchanged-showing .jsondiffpatch-unchanged,
  .jsondiffpatch-unchanged-showing .jsondiffpatch-movedestination > .jsondiffpatch-value {
    max-height: 100px;
  }
  .jsondiffpatch-unchanged-hidden .jsondiffpatch-unchanged,
  .jsondiffpatch-unchanged-hidden .jsondiffpatch-movedestination > .jsondiffpatch-value {
    max-height: 0;
  }
  .jsondiffpatch-unchanged-hiding .jsondiffpatch-movedestination > .jsondiffpatch-value,
  .jsondiffpatch-unchanged-hidden .jsondiffpatch-movedestination > .jsondiffpatch-value {
    display: block;
  }
  .jsondiffpatch-unchanged-visible .jsondiffpatch-unchanged,
  .jsondiffpatch-unchanged-visible .jsondiffpatch-movedestination > .jsondiffpatch-value {
    max-height: 100px;
  }
  .jsondiffpatch-unchanged-hiding .jsondiffpatch-unchanged,
  .jsondiffpatch-unchanged-hiding .jsondiffpatch-movedestination > .jsondiffpatch-value {
    max-height: 0;
  }
  .jsondiffpatch-unchanged-showing .jsondiffpatch-arrow,
  .jsondiffpatch-unchanged-hiding .jsondiffpatch-arrow {
    display: none;
  }
  .jsondiffpatch-value {
    display: inline-block;
  }
  .jsondiffpatch-property-name {
    display: inline-block;
    padding-right: 5px;
    vertical-align: top;
  }
  .jsondiffpatch-property-name:after {
    content: ': ';
  }
  .jsondiffpatch-child-node-type-array > .jsondiffpatch-property-name:after {
    content: ': [';
  }
  .jsondiffpatch-child-node-type-array:after {
    content: '],';
  }
  div.jsondiffpatch-child-node-type-array:before {
    content: '[';
  }
  div.jsondiffpatch-child-node-type-array:after {
    content: ']';
  }
  .jsondiffpatch-child-node-type-object > .jsondiffpatch-property-name:after {
    content: ': {';
  }
  .jsondiffpatch-child-node-type-object:after {
    content: '},';
  }
  div.jsondiffpatch-child-node-type-object:before {
    content: '{';
  }
  div.jsondiffpatch-child-node-type-object:after {
    content: '}';
  }
  .jsondiffpatch-value pre:after {
    content: ',';
  }
  li:last-child > .jsondiffpatch-value pre:after,
  .jsondiffpatch-modified > .jsondiffpatch-left-value pre:after {
    content: '';
  }
  .jsondiffpatch-modified .jsondiffpatch-value {
    display: inline-block;
  }
  .jsondiffpatch-modified .jsondiffpatch-right-value {
    margin-left: 5px;
  }
  .jsondiffpatch-moved .jsondiffpatch-value {
    display: none;
  }
  .jsondiffpatch-moved .jsondiffpatch-moved-destination {
    display: inline-block;
    background: #ffffbb;
    color: #888;
  }
  .jsondiffpatch-moved .jsondiffpatch-moved-destination:before {
    content: ' => ';
  }
  ul.jsondiffpatch-textdiff {
    padding: 0;
  }
  .jsondiffpatch-textdiff-location {
    color: #bbb;
    display: inline-block;
    min-width: 60px;
  }
  .jsondiffpatch-textdiff-line {
    display: inline-block;
  }
  .jsondiffpatch-textdiff-line-number:after {
    content: ',';
  }
  .jsondiffpatch-error {
    background: red;
    color: white;
    font-weight: bold;
  }
`;

// min-height messes up the container height if the contents are too tall
const StyledBanner = styled(Banner)`
  min-height: auto;
  padding: 15px ${Spacing[7]};
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ToggleLabel = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
  margin-left: ${Spacing[2]};
`;

const NoChangesWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledCheckIcon = styled(CircleCheckIcon)`
  color: ${({ theme }) => theme.color.variants.success.default};
  margin-right: ${Spacing[2]};
`;

const StyledAlertIcon = styled(CircleAlertIcon)<{ color?: string }>`
  color: ${({ color, theme }) => color || theme.color.variants.danger.default};
  margin-right: ${Spacing[2]};
`;

type SchemaObject = VerificationTemplateVersion | WorkflowVersion;
type SchemaObjDiffConfig = {
  currentId: string | undefined;
  comparedId: string | undefined;
  diffPatcher: DiffPatcher;
};
type SchemaObjIdPair = Pick<SchemaObjDiffConfig, 'currentId' | 'comparedId'>;

const getParentId = (schemaObject: SchemaObject): string =>
  schemaObject.type === ApiModel.WorkflowVersion
    ? schemaObject.relationships.workflow.data.id
    : schemaObject.relationships['verification-template'].data.id;

/**
 * Returns an array of diff configs for each object in the
 * current ITV's schema by attempting to find a pair in the compared
 * ITV's schema with the same parent object (two versions with the same parent = change made)
 *
 * The logic first creates a map of parent object ids to the current versions,
 * then reduces with the compared objects to finds pairs and one-offs (net-new, or deletions)
 *
 * Since the ITV and all schema objects are published together and become immutable,
 * we reliably know that two schema version objects with the same parent indicates
 * that a diff should be displayed.
 *
 * The possible scenarios are:
 * - current/compared tokens exist and are the same   --> no changes made
 * - current/compared tokens exists and are different --> updated, should diff
 * - current token exists but compared does not       --> newly added workflow version or VTV
 * - compared token exists but current does not       --> removed from prior version
 */
const getSchemaObjDiffConfigs = (
  currentWfvs: SchemaObject[],
  comparedWfvs: SchemaObject[],
): SchemaObjDiffConfig[] => {
  // create map of parent/version ids
  const currentMap: Record<string, SchemaObjIdPair> = currentWfvs.reduce(
    (map, wfv) => ({
      ...map,
      [getParentId(wfv)]: {
        currentId: wfv.id,
        comparedId: null,
      },
    }),
    {},
  );
  // reduce with compared schema objects, matching version id pairs or finding removed versions
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const jointMap = comparedWfvs.reduce((map, wfv) => {
    const parentId = getParentId(wfv);
    return {
      ...map,
      [parentId]:
        map[parentId] != null
          ? {
              ...map[parentId],
              comparedId: wfv.id,
            }
          : // add explicit null currentId so we can safely destructure within the component
            {
              currentId: null,
              comparedId: wfv.id,
            },
    };
  }, currentMap);
  // map with dispatcher - current schema objects that still don't have a pair are net-new
  return Object.values(jointMap).map((diffConfig) => ({
    ...diffConfig,
    diffPatcher: create({
      objectHash: function (obj: ActualWorkflowStep & VerificationCheckConfig, index: number) {
        // matching by name avoids visualizing a deleted step as an off-by-one diff for subsequent steps
        return obj.token || obj.check || '$$index:' + index;
      },
      array: {
        detectMove: false,
      },
    }),
  }));
};

type TemplateDiffConfig = {
  title: string;
  description?: string;
  // diff object which takes two configs:
  // (1) object matching function
  // (2) array handling config (primarily move handling)
  diffPatcher: DiffPatcher;
  attributeKeys: (keyof InquiryTemplateVersionAttributes)[];
  // links user to config section in editor to examine and/or make further changes
  editorPath: string;
};

export const DIFF_VIEW_CONFIGS: TemplateDiffConfig[] = [
  {
    title: 'General',
    description:
      'Name, display name, description, enabled locales, expiration intervals, and redirect configuration',
    diffPatcher: create(),
    attributeKeys: [
      'name',
      'name-display',
      'description',
      'enabled-locales',
      'expiration-intervals',
      'redirect-uri',
      'redirect-query-string-config',
    ],
    editorPath: 'config',
  },
  {
    title: 'Steps',
    diffPatcher: create({
      objectHash: function (obj: InquiryTemplateVersionStep, index: number) {
        // matching by name avoids visualizing a deleted step as an off-by-one diff for subsequent steps
        return obj.name || '$$index:' + index;
      },
      array: {
        detectMove: false,
      },
    }),
    attributeKeys: ['steps'],
    editorPath: 'flow',
  },
  {
    title: 'Theme',
    diffPatcher: create(),
    attributeKeys: ['theme'],
    editorPath: 'theme',
  },
  {
    title: 'Fields',
    diffPatcher: create({
      objectHash: function (obj: FieldSchema, index: number) {
        return obj.key || '$$index:' + index;
      },
      array: {
        detectMove: false,
      },
    }),
    attributeKeys: ['field-schema'],
    editorPath: 'config/fields',
  },
];

type DiffSection = 'template' | 'workflows' | 'verifications';

const BiometricConsentValidationBanner = ({
  message,
  backgroundColor,
  icon,
}: {
  message: string;
  backgroundColor: string;
  icon: ReactNode;
}) => {
  return (
    <StyledBanner backgroundColor={backgroundColor} icon={icon}>
      {message}
    </StyledBanner>
  );
};

const EnhancedUsagesBanner = ({
  enhancedUsages,
  pricingChangeRequired,
}: {
  enhancedUsages: EnhancedUsage[];
  pricingChangeRequired: boolean;
}) => {
  if (enhancedUsages.length === 0 || !pricingChangeRequired) {
    return <></>;
  }

  return (
    <StyledBanner
      backgroundColor={defaultTheme.color.variants.caution.highlightLight}
      icon={<StyledAlertIcon color={defaultTheme.color.variants.caution.default} />}
    >
      <InquiriesEnhancedUsagesList
        enhancedUsages={enhancedUsages}
        message={`On publish, this template will be upgraded to enhanced pricing tier due to the following usage${
          enhancedUsages.length > 1 ? 's' : ''
        }:`}
      />
    </StyledBanner>
  );
};

export const BiometricsConsentTextBanners = ({
  inquiryTemplateVersion,
}: {
  inquiryTemplateVersion: InquiryTemplateVersion;
}) => {
  const banners = [];
  const biometricConsentCompliance =
    inquiryTemplateVersion.attributes['biometric-consent-compliance'];
  const steps = inquiryTemplateVersion.attributes['steps'];
  const getDisplayNameForStepName = (stepName: string) => {
    const step = steps.find((s) => s.name === stepName);
    return getDisplayNameForStep(step);
  };

  const validation = biometricConsentCompliance['validation'];

  if (!validation['biometric-consent-text-required']) {
    banners.push(
      <BiometricConsentValidationBanner
        message='Biometric consent text not needed.'
        backgroundColor={defaultTheme.color.variants.success.highlightLight}
        icon={<StyledCheckIcon />}
      />,
    );
  } else if (biometricConsentCompliance['ignore-validation']) {
    banners.push(
      <BiometricConsentValidationBanner
        message='Biometric consent text validation required but ignored.'
        backgroundColor={defaultTheme.color.variants.caution.highlightLight}
        icon={<StyledAlertIcon color={defaultTheme.color.variants.caution.default} />}
      />,
    );
  } else if (validation['invalid-steps'].length === 0) {
    banners.push(
      <BiometricConsentValidationBanner
        message='Required biometric consent text detected.'
        backgroundColor={defaultTheme.color.variants.success.highlightLight}
        icon={<StyledCheckIcon />}
      />,
    );
  } else {
    validation['invalid-steps'].forEach((invalidStep: InvalidStep) => {
      if (
        invalidStep['reason'] ===
        BiometricConsentComplianceFailureReasons.ConsentTextNotPrecedingBiometricVerifications
      ) {
        banners.push(
          <BiometricConsentValidationBanner
            message={`Required biometric consent text not detected for step '${getDisplayNameForStepName(
              invalidStep['name'],
            )}'. Please ensure consent text is shown on a preceding screen.`}
            backgroundColor={defaultTheme.color.variants.danger.highlightLight}
            icon={<StyledAlertIcon />}
          />,
        );
      }
      if (
        invalidStep['reason'] ===
        BiometricConsentComplianceFailureReasons.ConsentTextNotPrecedingSubmitButton
      ) {
        banners.push(
          <BiometricConsentValidationBanner
            message={`Biometric consent text detected below Submit button for step '${getDisplayNameForStepName(
              invalidStep['name'],
            )}'. Please ensure that biometric consent text is located above all Submit buttons.`}
            backgroundColor={defaultTheme.color.variants.danger.highlightLight}
            icon={<StyledAlertIcon />}
          />,
        );
      }
    });
  }

  return (
    <>
      {banners.map((banner, idx) => (
        // For some reason, this parent div is required for handling overflow and
        // expanding the banner vertically
        <div key={idx}>{banner}</div>
      ))}
    </>
  );
};

export interface InquiryTemplateJsonVersionDiffProps {
  inquiryTemplate: InquiryTemplate;
  // version and schema currently viewed in ITE
  currentVersion: InquiryTemplateVersion;
  currentSchema: InquiryTemplateSchema;
  // currently the compared version is always latest-published,
  // but the intention is to support diffing against
  // any selected version in the future as well.
  comparedVersion: InquiryTemplateVersion | undefined;
  comparedSchema: InquiryTemplateSchema;
  enhancedUsageBillingEnabled: boolean;
  loading: boolean;
  handleClose: () => void;
}

const InquiryTemplateJsonVersionDiff = ({
  inquiryTemplate,
  currentVersion,
  currentSchema,
  comparedVersion,
  comparedSchema,
  enhancedUsageBillingEnabled,
  loading,
  handleClose,
}: InquiryTemplateJsonVersionDiffProps) => {
  const navigate = useNavigate();
  const [diffSection, setDiffSection] = useState<DiffSection>('template');
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [activeScrollItemId, setActiveScrollItemId] = useState<string | undefined>(null);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const clearScrollItemId = useCallback(() => setActiveScrollItemId(null), []);
  const refMap: ScrollRefMap = {};
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const loadingOrErrored = loading || comparedVersion == null;

  const handleSetDiffSection = useCallback((section: DiffSection) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    rightColumnRef.current.scrollTo({ top: 0, behavior: 'auto' });
    setDiffSection(section);
  }, []);

  const workflowVersionDiffConfigs = useMemo(
    () =>
      getSchemaObjDiffConfigs(
        Object.values(currentSchema.workflowVersions),
        Object.values(comparedSchema.workflowVersions),
      ),
    [currentSchema.workflowVersions, comparedSchema.workflowVersions],
  );

  const vtmplvDiffConfigs = useMemo(
    () =>
      getSchemaObjDiffConfigs(
        Object.values(currentSchema.verificationTemplateVersions),
        Object.values(comparedSchema.verificationTemplateVersions),
      ),
    [currentSchema.verificationTemplateVersions, comparedSchema.verificationTemplateVersions],
  );

  const getDiffNavItemConfig = useCallback(
    (diffConfigs: SchemaObjDiffConfig[], objType: keyof InquiryTemplateSchema) =>
      diffConfigs.map(({ currentId, comparedId }) => {
        const id = currentId ?? comparedId;
        const sectionRef = createRef<HTMLDivElement>();
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        refMap[id] = sectionRef;
        let label = id;
        if (objType === 'verificationTemplateVersions') {
          const type =
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            currentSchema.verificationTemplateVersions[currentId]?.type ??
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            comparedSchema.verificationTemplateVersions[comparedId]?.type;
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          label = VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT[type];
        }
        return {
          id,
          label,
          withInstantScroll: true,
          scrollConfig: {
            sectionRef: sectionRef,
            columnRef: rightColumnRef,
          },
          setActiveScrollItemId: setActiveScrollItemId,
        };
      }),
    [
      currentSchema.verificationTemplateVersions,
      comparedSchema.verificationTemplateVersions,
      refMap,
    ],
  );

  const [workflowVersionDiffItems, vtmplvDiffItems] = useMemo<
    [GroupScrollItemProps[], GroupScrollItemProps[]]
  >(
    () => [
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      getDiffNavItemConfig(workflowVersionDiffConfigs, 'workflowVersions'),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      getDiffNavItemConfig(vtmplvDiffConfigs, 'verificationTemplateVersions'),
    ],
    [getDiffNavItemConfig, vtmplvDiffConfigs, workflowVersionDiffConfigs],
  );

  const handleNavToConfig = useCallback(
    (path: string) => {
      handleClose();
      navigate(
        `/dashboard/inquiry-templates/${currentVersion?.relationships['inquiry-template'].data.id}/${currentVersion?.id}/${path}`,
      );
    },
    [currentVersion, handleClose, history],
  );

  const hasWorkflows =
    Object.keys({
      ...currentSchema.workflowVersions,
      ...comparedSchema.workflowVersions,
    }).length > 0;
  const hasVerifications =
    Object.keys({
      ...currentSchema.verificationTemplateVersions,
      ...comparedSchema.verificationTemplateVersions,
    }).length > 0;
  const navItems = [
    <DiffNavItem
      key='diff-modal__nav-item-template'
      id='diff-modal__nav-item-template'
      aria-selected={!loadingOrErrored && diffSection === 'template'}
      disabled={loadingOrErrored}
      onClick={() => {
        clearScrollItemId();
        handleSetDiffSection('template');
      }}
    >
      Template
    </DiffNavItem>,
    hasWorkflows ? (
      <EditorNavItemGroup
        label='Workflows'
        key='diff-modal__nav-item-workflows'
        id='diff-modal__nav-item-workflows'
        disabled={loadingOrErrored}
        items={workflowVersionDiffItems}
        activeScrollItemId={activeScrollItemId}
        setActiveScrollItemId={setActiveScrollItemId}
        onClick={() => handleSetDiffSection('workflows')}
      />
    ) : null,
    hasVerifications ? (
      <EditorNavItemGroup
        label='Verifications'
        key='diff-modal__nav-item-verifications'
        id='diff-modal__nav-item-verifications'
        disabled={loadingOrErrored}
        items={vtmplvDiffItems}
        activeScrollItemId={activeScrollItemId}
        setActiveScrollItemId={setActiveScrollItemId}
        onClick={() => handleSetDiffSection('verifications')}
      />
    ) : null,
  ].filter(Boolean);

  const templateDiffContent = useMemo(
    () =>
      loadingOrErrored ? null : (
        <>
          {DIFF_VIEW_CONFIGS.map(
            ({ title, description, diffPatcher, attributeKeys, editorPath }) => {
              const currentObject = pick(attributeKeys, currentVersion.attributes);
              const comparedObject = pick(attributeKeys, comparedVersion.attributes);
              return (
                <DiffViewCard
                  key={title}
                  title={title}
                  description={description}
                  diff={diffPatcher.diff(comparedObject, currentObject)}
                  baseObject={comparedObject}
                  editorPath={editorPath}
                  handleNavToConfig={handleNavToConfig}
                />
              );
            },
          )}
        </>
      ),
    [currentVersion.attributes, comparedVersion, handleNavToConfig, loadingOrErrored],
  );

  const workflowsDiffContent = useMemo(
    () => (
      <>
        {workflowVersionDiffConfigs.map(({ currentId, comparedId, diffPatcher }) => {
          const currentObject =
            currentId != null
              ? pick(['steps-config'], currentSchema.workflowVersions[currentId].attributes)
              : {};
          const comparedObject =
            comparedId != null
              ? pick(['steps-config'], comparedSchema.workflowVersions[comparedId].attributes)
              : {};
          const activeId = currentId ?? comparedId;
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          const activeWfvInitialToken = (currentSchema.workflowVersions[currentId]?.attributes ??
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            comparedSchema.workflowVersions[comparedId]?.attributes)?.['steps-config']?.[0]?.token;
          const editorPath =
            activeWfvInitialToken != null
              ? `actions/${activeId}/${activeWfvInitialToken}`
              : `actions/${activeId}`;
          return (
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            <ScrollWrapper key={activeId} ref={refMap[activeId]}>
              <DiffViewCard
                title={`Workflow (${activeId})`}
                description='Step configurations'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                status={
                  isEmpty(comparedObject) ? 'success' : isEmpty(currentObject) ? 'danger' : null
                }
                diff={diffPatcher.diff(comparedObject, currentObject)}
                baseObject={comparedObject}
                editorPath={editorPath}
                handleNavToConfig={handleNavToConfig}
              />
            </ScrollWrapper>
          );
        })}
      </>
    ),
    [
      currentSchema.workflowVersions,
      comparedSchema.workflowVersions,
      handleNavToConfig,
      workflowVersionDiffConfigs,
      refMap,
    ],
  );

  const vtmplvsDiffContent = useMemo(
    () => (
      <>
        {vtmplvDiffConfigs.map(({ currentId, comparedId, diffPatcher }) => {
          const currentObject =
            currentId != null
              ? omit(
                  ['created-at', 'updated-at', 'status', 'name'],
                  currentSchema.verificationTemplateVersions[currentId].attributes,
                )
              : {};
          const comparedObject =
            comparedId != null
              ? omit(
                  ['created-at', 'updated-at', 'status', 'name'],
                  comparedSchema.verificationTemplateVersions[comparedId].attributes,
                )
              : {};
          const activeId = currentId ?? comparedId;
          const currentVtmplv =
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            currentSchema.verificationTemplateVersions[currentId] ??
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            comparedSchema.verificationTemplateVersions[comparedId];
          return (
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            <ScrollWrapper key={activeId} ref={refMap[activeId]}>
              <DiffViewCard
                title={getVtmplvDisplayName(currentVtmplv)}
                description='Checks and verification type-specific configurations'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                status={
                  isEmpty(comparedObject) ? 'success' : isEmpty(currentObject) ? 'danger' : null
                }
                diff={diffPatcher.diff(comparedObject, currentObject)}
                baseObject={comparedObject}
                editorPath={`config/verifications/${activeId}`}
                handleNavToConfig={handleNavToConfig}
              />
            </ScrollWrapper>
          );
        })}
      </>
    ),
    [
      currentSchema.verificationTemplateVersions,
      comparedSchema.verificationTemplateVersions,
      handleNavToConfig,
      vtmplvDiffConfigs,
      refMap,
    ],
  );

  const currentDiffContent = useMemo(() => {
    if (loading) {
      return (
        <NoContentContainer>
          <LoadingIndicator />
        </NoContentContainer>
      );
    } else if (comparedVersion == null) {
      return (
        <NoContentContainer>
          <Paragraph size='lg'>{LOAD_VERSION_DIFF_ERROR_MESSAGE}</Paragraph>
        </NoContentContainer>
      );
    }
    switch (diffSection) {
      case 'template':
        return templateDiffContent;
      case 'workflows':
        return workflowsDiffContent;
      case 'verifications':
        return vtmplvsDiffContent;
      default:
        assertNever(diffSection);
    }
  }, [
    comparedVersion,
    diffSection,
    loading,
    templateDiffContent,
    vtmplvsDiffContent,
    workflowsDiffContent,
  ]);

  return (
    <Container>
      <LeftColumnContainer>
        <LeftColumn>
          <Label size='sm' color='tertiary'>
            Changes
          </Label>
          {navItems}
        </LeftColumn>
      </LeftColumnContainer>
      <RightColumnContainer>
        <EnhancedUsagesBanner
          enhancedUsages={currentVersion.attributes['enhanced-usages']}
          pricingChangeRequired={
            inquiryTemplate.attributes['pricing-tier'] !== 'enhanced' &&
            currentVersion.attributes.tier === InquiryTemplateVersionTier.Enhanced &&
            enhancedUsageBillingEnabled
          }
        />
        <BiometricsConsentTextBanners inquiryTemplateVersion={currentVersion} />
        <RightColumn ref={rightColumnRef}>{currentDiffContent}</RightColumn>
      </RightColumnContainer>
    </Container>
  );
};

type DiffComparisonObject = Partial<
  | Pick<InquiryTemplateVersionAttributes, keyof InquiryTemplateVersionAttributes>
  | Pick<WorkflowVersionAttributes, keyof WorkflowVersionAttributes>
  | Pick<VerificationTemplateVersionAttributes, keyof VerificationTemplateVersionAttributes>
>;

type DiffObjStatus = Extract<Variant, 'success' | 'danger'>;

interface DiffViewCardProps {
  title: string;
  description?: string;
  status?: DiffObjStatus;
  diff: Delta | undefined;
  baseObject: DiffComparisonObject;
  editorPath?: string;
  handleNavToConfig?: (path: string) => void;
}

const DiffDescription = styled(Paragraph).attrs(() => ({ size: 'md', color: 'tertiary' }))`
  margin-top: -${Spacing[4]};
  margin-bottom: ${Spacing[2]};
`;

export const DiffViewCard = ({
  title,
  description,
  status,
  diff,
  baseObject,
  editorPath,
  handleNavToConfig,
}: DiffViewCardProps) => {
  const [showUnchanged, { toggle: toggleShowUnchanged }] = useToggle(false);
  const toggleLabel = showUnchanged ? 'Hide unchanged' : 'Show unchanged';
  const diffHTML =
    diff != null ? formatters.html.format(diff, showUnchanged ? baseObject : {}) : null;
  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={
            handleNavToConfig &&
            editorPath && (
              <Button
                width='auto'
                variant='basic'
                aria-label='View or Edit'
                id='diff-view-card__view-or-edit-button'
                onClick={() => handleNavToConfig(editorPath)}
              >
                View or Edit
              </Button>
            )
          }
        >
          {title}
          {status != null && (
            <Sticker variant={status}>{status === 'success' ? 'New' : 'Deleted'}</Sticker>
          )}
        </Card.Title>
        {description != null && <DiffDescription>{description}</DiffDescription>}
        {diff != null ? (
          <>
            <ToggleWrapper>
              <ToggleSlider
                aria-label={toggleLabel}
                value={showUnchanged}
                onChange={toggleShowUnchanged}
              />
              <ToggleLabel>{toggleLabel}</ToggleLabel>
            </ToggleWrapper>
            <DiffContainer dangerouslySetInnerHTML={{ __html: dompurify.sanitize(diffHTML) }} />
          </>
        ) : (
          <NoChangesWrapper>
            <StyledCheckIcon size={24} />
            <Paragraph size='md'>No changes</Paragraph>
          </NoChangesWrapper>
        )}
      </Card.Body>
    </Card>
  );
};

export default InquiryTemplateJsonVersionDiff;
